/**
 * ForgeCoachHome — /forge-coach/about
 *
 * Public positioning page for Forge Coach (D-44). Mirrors the *Home pattern
 * used by the AI Interview Coach and the other products so a cold visitor
 * lands on a marketing surface that explains the product before hitting the
 * auth/tier gate on the /forge-coach work surface.
 *
 * Accent uses the theme `secondary` palette to match the existing Forge Coach
 * surfaces (the tier/sign-in gate on ForgeCoachPage also uses secondary).
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Trans, useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PsychologyIcon from "@mui/icons-material/Psychology";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const WORK_SURFACE = "/forge-coach";

const FEATURE_KEYS = [
  {
    title: "ForgeCoachHome.featureDossierTitle",
    body: "ForgeCoachHome.featureDossierBody",
  },
  {
    title: "ForgeCoachHome.featurePhasesTitle",
    body: "ForgeCoachHome.featurePhasesBody",
  },
  {
    title: "ForgeCoachHome.featureMemoryTitle",
    body: "ForgeCoachHome.featureMemoryBody",
  },
  {
    title: "ForgeCoachHome.featurePhaseAwareTitle",
    body: "ForgeCoachHome.featurePhaseAwareBody",
  },
] as const;

export default function ForgeCoachHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation("pages-core");

  const goToPrimaryCta = () => {
    if (isAuthenticated) {
      navigate(WORK_SURFACE);
    } else {
      navigate("/login", { state: { from: WORK_SURFACE } });
    }
  };

  const primaryCtaLabel = isAuthenticated
    ? t("ForgeCoachHome.primaryCtaAuthenticatedLabel")
    : t("ForgeCoachHome.primaryCtaUnauthenticatedLabel");

  const accent = theme.palette.secondary.main;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.15)}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="flex-start">
            <Chip
              icon={
                <PsychologyIcon
                  sx={{ fontSize: "0.95rem !important", color: `${accent} !important` }}
                />
              }
              label={t("ForgeCoachHome.heroChipLabel")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.08em",
                bgcolor: alpha(accent, 0.12),
                color: accent,
              }}
            />
            <Typography
              variant="h2"
              fontWeight={500}
              sx={{
                letterSpacing: "-0.03em",
                fontSize: { xs: "2.4rem", md: "3.2rem" },
                maxWidth: 900,
              }}
            >
              <Trans
                i18nKey="ForgeCoachHome.heroTitleLine1"
                t={t}
                components={[<Box key="span0" component="span" sx={{ color: accent }} />]}
              />
              <br />
              {t("ForgeCoachHome.heroTitleLine2")}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, maxWidth: 780, lineHeight: 1.5 }}
            >
              {t("ForgeCoachHome.heroSubtitle")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={goToPrimaryCta}
                sx={{ fontWeight: 500, px: 3 }}
              >
                {primaryCtaLabel}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/pricing")}
                sx={{
                  fontWeight: 500,
                  px: 3,
                  borderColor: alpha(accent, 0.5),
                  color: accent,
                  "&:hover": { borderColor: accent, bgcolor: alpha(accent, 0.04) },
                }}
              >
                {t("ForgeCoachHome.seePricingButton")}
              </Button>
            </Stack>
            <Stack direction="row" spacing={3} mt={3} flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: accent }} />
                <Typography variant="body2" color="text.secondary">
                  {t("ForgeCoachHome.trustChipProAndAllAccess")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: accent }} />
                <Typography variant="body2" color="text.secondary">
                  {t("ForgeCoachHome.trustChipEightPhasePlan")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: accent }} />
                <Typography variant="body2" color="text.secondary">
                  {t("ForgeCoachHome.trustChipRemembersEverySession")}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* What it is */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1} mb={5}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: accent, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("ForgeCoachHome.whatItIsEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("ForgeCoachHome.whatItIsTitle")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 780, lineHeight: 1.65 }}
          >
            {t("ForgeCoachHome.whatItIsIntro")}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
          }}
        >
          {FEATURE_KEYS.map((f) => (
            <Paper
              key={f.title}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <CheckCircleIcon sx={{ color: accent, fontSize: 22, mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" fontWeight={500} gutterBottom>
                    {t(f.title)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {t(f.body)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            p: { xs: 4, md: 6 },
            background: `linear-gradient(135deg, ${alpha(accent, 0.06)} 0%, ${alpha(accent, 0.02)} 100%)`,
            border: `1px solid ${alpha(accent, 0.2)}`,
            borderRadius: 3,
          }}
        >
          <PsychologyIcon sx={{ fontSize: 40, color: accent, mb: 2 }} />
          <Typography variant="h4" fontWeight={500} gutterBottom sx={{ letterSpacing: "-0.02em" }}>
            {t("ForgeCoachHome.finalCtaTitle")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 560, mx: "auto", mb: 4 }}
          >
            {t("ForgeCoachHome.finalCtaBody")}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={goToPrimaryCta}
            sx={{ fontWeight: 500 }}
          >
            {primaryCtaLabel}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
